// App.js
import React, { Suspense, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import Preloader from "./components/PreLoader";
import { AppProvider } from "./core/context/AppContext";
import { useScrollToTop } from "./core/hooks";
import ErrorBoundary from "./components/ErrorBoundary";
import { Notification } from "./components/Notification";
import SoftwareDevLoader from "./components/animations/SoftwareDevLoader";

const Navbar = React.lazy(() => import("./layout/Navbar"));
const Footer = React.lazy(() => import("./layout/Footer"));
const AppRoutes = React.lazy(() => import("./routes/AppRoutes"));

// Component to handle route-specific loading
const RouteBasedLoader = ({ children }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const isSoftwareDevelopmentRoute = location.pathname === "/software-development";
  
  // Reset loading state whenever the route changes
  useEffect(() => {
    if (isSoftwareDevelopmentRoute) {
      setLoading(true); // Always show loader when navigating to software development
    } else {
      setLoading(false); // Don't show the special loader for other routes
    }
  }, [location.pathname, isSoftwareDevelopmentRoute]);
  
  // Handle loading completion
  const handleLoadingComplete = () => {
    setLoading(false);
  };
  
  if (loading && isSoftwareDevelopmentRoute) {
    return <SoftwareDevLoader onLoadingComplete={handleLoadingComplete} />;
  }
  
  return children;
};

function App() {
  const { i18n } = useTranslation();
  const language = i18n.language;
  useScrollToTop();

  return (
    <ErrorBoundary>
      <AppProvider>
        <div
          className={`w-full overflow-hidden font-northuraRegular ${
            language === "en" ? "font-light" : "font-bold"
          }`}
          dir={`${language === "en" ? "ltr" : "rtl"}`}
        >
          <Suspense fallback={<Preloader />}>
            <RouteBasedLoader>
              <Navbar />
              <AppRoutes />
              <Footer />
              <Notification />
            </RouteBasedLoader>
          </Suspense>
        </div>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
